import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';
import { Icon } from '../../icons';
import { Badge, Btn, Card, MetricCard, Alert, SectionHeader, DataTable, FormRow, Field, Tabs, FileUpload, Modal, FormActions } from '../../components/ui';
import { ConfirmModal } from '../../components/shared';
import * as vendorService from '../../api/vendors';
import { uploadFiles } from '../../api/files';
import { toast } from 'react-hot-toast';
import { getFieldError } from '../../utils/apiUtils';
import { formatDateByPreference, formatCompactRupees } from '../../utils/formatters';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

export default function VendorsScreen() {
  const { user } = useAuth();
  const isAdmin = user?.roles_list?.some(r => ['super_admin', 'admin'].includes(r));
  const isMember = (user?.primary_role_label === 'Member' || user?.roles_list?.includes('member')) && !isAdmin;

  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.vendors?.[key] || translations.en?.vendors?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_vendors: 0, active_amcs: 0, expiring_soon: 0, annual_amc_cost: 0 });
  const [modal, setModal] = useState(null);
  const [editId, setEditId] = useState(null);
  const [delItem, setDelItem] = useState(null);
  const defV = { name: "", cat: "", contact: "", phone: "", email: "", address: "", gst: "", notes: "", status: "Active", tds_applicable: "No", tds_rate: "", pan: "", pan_files: [], pan_file_ids: [] };
  const defA = { vendor_id: "", service_type: "", start_date: "", end_date: "", contract_value: "", billing_cycle: "Yearly", reminder_days: "30", files: [], file_ids: [] };
  const [vForm, setVForm] = useState(defV);
  const [aForm, setAForm] = useState(defA);
  const [vendors, setVendors] = useState([]);
  const [amcs, setAmcs] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, aRes, sRes] = await Promise.all([
        vendorService.getVendors(),
        vendorService.getAmcContracts(),
        vendorService.getVendorStats()
      ]);
      setVendors(vRes.data || []);
      setAmcs(aRes.data || []);
      setStats(sRes.data || { total_vendors: 0, active_amcs: 0, expiring_soon: 0, annual_amc_cost: 0 });
    } catch (error) {
      console.error("Error loading vendors data:", error);
      toast.error(t("toast_load_failed"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    loadData();
  }, [loadData]);

  const sv = k => e => {
    let val = typeof e === "object" && e.target ? e.target.value : e;
    if (k === "name" && typeof val === "string") {
      val = val.replace(/[0-9]/g, '');
    }
    setVForm(f => ({ ...f, [k]: val }));
    setFormErrors(prev => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      delete next[k];
      if (k === "cat") delete next.category;
      if (k === "gst") delete next.gst_number;
      if (k === "pan") delete next.pan_number;
      if (k === "contact") delete next.contact_person;
      return next;
    });
  };

  const handlePanChange = e => {
    let raw = (e.target ? e.target.value : e) || '';
    raw = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '');

    setVForm(current => {
      let val = raw;
      const prev = current.pan || '';
      // If previous value had letters, and input starts with digits (e.g. mobile keyboard wiped previous text on mode switch)
      if (prev && /^[A-Z]+$/.test(prev) && /^\d+$/.test(raw)) {
        val = prev + raw;
      } else if (prev && /^[A-Z]{5}\d+$/.test(prev) && /^[A-Z]+$/.test(raw) && raw.length <= 2) {
        val = prev + raw;
      }
      val = val.slice(0, 10);
      return { ...current, pan: val };
    });

    setFormErrors(prev => {
      if (!prev || (!prev.pan && !prev.pan_number)) return prev;
      const next = { ...prev };
      delete next.pan;
      delete next.pan_number;
      return next;
    });
  };

  const handleGstChange = e => {
    let raw = (e.target ? e.target.value : e) || '';
    raw = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '');

    setVForm(current => {
      let val = raw;
      const prev = current.gst || '';
      if (prev && /^\d+$/.test(prev) && /^[A-Z]+$/.test(raw)) {
        val = prev + raw;
      } else if (prev && /^\d{2}[A-Z]+$/.test(prev) && /^\d+$/.test(raw)) {
        val = prev + raw;
      }
      val = val.slice(0, 15);
      return { ...current, gst: val };
    });

    setFormErrors(prev => {
      if (!prev || (!prev.gst && !prev.gst_number)) return prev;
      const next = { ...prev };
      delete next.gst;
      delete next.gst_number;
      return next;
    });
  };

  const sa = k => e => {
    const val = typeof e === "object" && e.target ? e.target.value : e;
    setAForm(f => ({ ...f, [k]: val }));
    if (formErrors[k]) setFormErrors(prev => ({ ...prev, [k]: null }));
  };
  const daysLeft = ds => {
    if (!ds) return 0;
    return Math.ceil((new Date(ds) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const validateVendor = () => {
    const errors = {};
    const trimmedName = vForm.name?.trim();
    if (!trimmedName) {
      errors.name = t("val_vendor_name");
    } else if (/\d/.test(trimmedName)) {
      errors.name = t("val_vendor_name_numbers", "Vendor name cannot contain numbers");
    } else if (!/\p{L}/u.test(trimmedName)) {
      errors.name = t("val_vendor_name_letters", "Vendor name cannot be numeric only");
    } else {
      const duplicate = vendors.some(
        v => (!editId || v.id !== editId) && v.name?.trim().toLowerCase() === trimmedName.toLowerCase()
      );
      if (duplicate) {
        errors.name = t("val_vendor_name_exists");
      }
    }

    if (!vForm.cat) {
      errors.cat = t("val_cat");
    }

    if (vForm.phone?.trim()) {
      const phoneClean = vForm.phone.trim().replace(/[\s-]/g, "");
      if (!/^\+?[0-9]{7,15}$/.test(phoneClean)) {
        errors.phone = t("val_phone");
      }
    }

    if (vForm.email?.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vForm.email.trim())) {
        errors.email = t("val_email");
      }
    }

    if (vForm.gst?.trim()) {
      const gstClean = vForm.gst.trim().toUpperCase();
      if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i.test(gstClean)) {
        errors.gst = t("val_gst");
      }
    }

    if (vForm.pan?.trim()) {
      const panClean = vForm.pan.trim().toUpperCase();
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(panClean)) {
        errors.pan = t("val_pan");
      }
    }

    if (vForm.tds_applicable === "Yes") {
      if (vForm.tds_rate === "" || vForm.tds_rate === null || vForm.tds_rate === undefined) {
        errors.tds_rate = t("val_tds");
      } else {
        const rate = parseFloat(vForm.tds_rate);
        if (isNaN(rate) || rate < 0 || rate > 100) {
          errors.tds_rate = t("val_tds_range");
        }
      }
    }

    setFormErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    if (!isValid) toast.error(t("toast_fill_required"));
    return isValid;
  };

  const validateAmc = () => {
    const errors = {};
    if (!aForm.vendor_id) errors.vendor_id = t("val_vendor_id");
    if (!aForm.service_type?.trim()) errors.service_type = t("val_service_type");
    if (!aForm.contract_value || parseFloat(aForm.contract_value) <= 0) errors.contract_value = t("val_contract_value");
    if (!aForm.end_date) errors.end_date = t("val_end_date");

    setFormErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    if (!isValid) toast.error(t("toast_fill_required"));
    return isValid;
  };

  const saveVendor = async () => {
    if (!validateVendor()) return;
    setSubmitting(true);
    try {
      const existingPanFileIds = (vForm.pan_files || [])
        .filter(f => !(f instanceof File) && f.id)
        .map(f => f.id);

      let panFileIds = [...existingPanFileIds];

      const newFiles = (vForm.pan_files || []).filter(f => f instanceof File);
      if (newFiles.length > 0) {
        const uploadRes = await uploadFiles(newFiles, 'vendors');
        if (uploadRes.success) {
          const uploaded = uploadRes.data?.uploaded || uploadRes.uploaded || [];
          panFileIds = [...panFileIds, ...uploaded.map(f => f.id)];
        }
      }

      const payload = {
        name: vForm.name?.trim(),
        category: vForm.cat,
        contact_person: vForm.contact?.trim() || null,
        phone: vForm.phone?.trim() || null,
        email: vForm.email?.trim() || null,
        address: vForm.address?.trim() || null,
        gst_number: vForm.gst?.trim() ? vForm.gst.trim().toUpperCase() : null,
        notes: vForm.notes?.trim() || null,
        status: vForm.status,
        tds_applicable: vForm.tds_applicable,
        tds_rate: vForm.tds_applicable === "Yes" && vForm.tds_rate ? parseFloat(vForm.tds_rate) : null,
        pan_number: vForm.pan?.trim() ? vForm.pan.trim().toUpperCase() : null,
        pan_file_ids: panFileIds
      };

      if (editId) {
        await vendorService.updateVendor(editId, payload);
        toast.success(t("toast_vendor_updated"));
      } else {
        await vendorService.createVendor(payload);
        toast.success(t("toast_vendor_created"));
      }

      setModal(null);
      setEditId(null);
      setVForm(defV);
      setFormErrors({});
      loadData();
    } catch (error) {
      console.error("Error saving vendor:", error);
      if (error.response?.status === 422) {
        setFormErrors(error.response.data.errors);
        toast.error(t("toast_val_errors"));
      } else {
        toast.error(error.message || t("toast_vendor_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const saveAmc = async () => {
    if (!validateAmc()) return;
    setSubmitting(true);
    try {
      const existingAmcFileIds = (aForm.files || [])
        .filter(f => !(f instanceof File) && f.id)
        .map(f => f.id);

      let amcFileIds = [...existingAmcFileIds];

      const newFiles = aForm.files?.filter(f => f instanceof File);
      if (newFiles && newFiles.length > 0) {
        const uploadRes = await uploadFiles(newFiles, 'amc_contracts');
        if (uploadRes.success) {
          const uploaded = uploadRes.data?.uploaded || uploadRes.uploaded || [];
          amcFileIds = [...amcFileIds, ...uploaded.map(f => f.id)];
        }
      }

      const payload = {
        vendor_id: parseInt(aForm.vendor_id),
        service_type: aForm.service_type,
        start_date: aForm.start_date,
        end_date: aForm.end_date,
        contract_value: parseFloat(aForm.contract_value) || 0,
        billing_cycle: aForm.billing_cycle,
        reminder_days: parseInt(aForm.reminder_days) || 30,
        file_ids: amcFileIds
      };

      if (editId) {
        await vendorService.updateAmcContract(editId, payload);
        toast.success(t("toast_amc_updated"));
      } else {
        await vendorService.createAmcContract(payload);
        toast.success(t("toast_amc_created"));
      }

      setModal(null);
      setEditId(null);
      setAForm(defA);
      setFormErrors({});
      loadData();
    } catch (error) {
      console.error("Error saving AMC:", error);
      if (error.response?.status === 422) {
        setFormErrors(error.response.data.errors);
        toast.error(t("toast_val_errors"));
      } else {
        toast.error(error.message || t("toast_amc_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDel = async () => {
    try {
      if (delItem.type === "vendor") {
        await vendorService.deleteVendor(delItem.id);
        toast.success(t("toast_vendor_deleted"));
      } else {
        await vendorService.deleteAmcContract(delItem.id);
        toast.success(t("toast_amc_deleted"));
      }
      setDelItem(null);
      loadData();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error(t("toast_del_failed"));
    }
  };

  const openEditV = v => {
    setEditId(v.id);
    setVForm({
      name: v.name,
      cat: v.category,
      contact: v.contact_person || "",
      phone: v.phone || "",
      email: v.email || "",
      address: v.address || "",
      gst: v.gst_number || "",
      notes: v.notes || "",
      status: v.status,
      tds_applicable: v.tds_applicable || "No",
      tds_rate: v.tds_rate || "",
      pan: v.pan_number || "",
      pan_files: v.pan_files || [],
      pan_file_ids: v.pan_file_ids || []
    });
    setModal("vendor");
  };

  const openEditA = a => {
    setEditId(a.id);
    setAForm({
      vendor_id: String(a.vendor_id),
      service_type: a.service_type,
      start_date: a.start_date?.split('T')[0] || "",
      end_date: a.end_date?.split('T')[0] || "",
      contract_value: String(a.contract_value),
      billing_cycle: a.billing_cycle,
      reminder_days: String(a.reminder_days),
      files: a.attachments || [],
      file_ids: a.file_ids || []
    });
    setModal("amc");
  };

  const maskPan = pan => {
    if (!pan || pan.length < 6) return pan || "-";
    return pan.slice(0, 5) + "***" + pan.slice(-1);
  };

  if (isMember) {
    return (
      <div className="fade-up flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center space-y-3 max-w-sm">
          <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
             <Icon name="shield" size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-ink dark:text-white">{t("msg_restricted")}</h2>
          <p className="text-muted dark:text-slate-400">{t("msg_restricted_desc")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up space-y-4">
      {delItem && <ConfirmModal title={t("title_delete", { type: delItem.type === "vendor" ? t("type_vendor") : t("type_amc") })} message={t("msg_delete_confirm", { type: delItem.type === "vendor" ? t("type_vendor_lc") : t("type_amc_lc") })} onConfirm={confirmDel} onCancel={() => setDelItem(null)} />}
      {modal === "vendor" && (
        <Modal title={editId ? t("title_edit_vendor") : t("title_add_vendor")} onClose={() => { setModal(null); setEditId(null); setFormErrors({}); }}>
          <FormRow cols={2}>
            <Field label={t("label_vendor_name")} value={vForm.name} onChange={sv("name")} required error={getFieldError(formErrors, 'name')} />
            <Field label={t("label_category")} type="select" value={vForm.cat} onChange={sv("cat")} options={[{ value: "", label: t("opt_select_category") }, t("cat_electrical"), t("cat_plumbing"), t("cat_security"), t("cat_housekeeping"), t("cat_lift"), t("cat_generator"), t("cat_cctv"), t("cat_fire"), t("cat_other")]} required error={getFieldError(formErrors, 'cat') || getFieldError(formErrors, 'category')} />
          </FormRow>
          <FormRow cols={3}>
            <Field label={t("label_contact_person")} value={vForm.contact} onChange={sv("contact")} error={getFieldError(formErrors, 'contact') || getFieldError(formErrors, 'contact_person')} />
            <Field label={t("label_phone")} value={vForm.phone} onChange={sv("phone")} error={getFieldError(formErrors, 'phone')} />
            <Field label={t("label_email")} type="email" value={vForm.email} onChange={sv("email")} error={getFieldError(formErrors, 'email')} />
          </FormRow>
          <FormRow cols={2}>
            <Field label={t("label_address")} value={vForm.address} onChange={sv("address")} error={getFieldError(formErrors, 'address')} />
            <Field 
              label={t("label_gst")} 
              value={vForm.gst} 
              onChange={handleGstChange} 
              placeholder={t("placeholder_gst")} 
              maxLength={15}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck="false"
              autoComplete="off"
              style={{ textTransform: "uppercase" }}
              className="uppercase font-mono"
              error={getFieldError(formErrors, 'gst') || getFieldError(formErrors, 'gst_number')} 
            />
          </FormRow>
          <FormRow cols={2}>
            <Field label={t("label_tds_app")} type="select" value={vForm.tds_applicable} onChange={sv("tds_applicable")} options={[{ value: "No", label: t("opt_no") }, { value: "Yes", label: t("opt_yes") }]} />
            {vForm.tds_applicable === "Yes" ? (
              <Field label={t("label_tds_rate")} type="number" placeholder={t("placeholder_tds")} value={vForm.tds_rate} onChange={sv("tds_rate")} required error={getFieldError(formErrors, 'tds_rate')} />
            ) : (
              <Field label={t("label_tds_rate")} value={t("label_na")} disabled />
            )}
          </FormRow>
          <FormRow cols={2}>
            <Field 
              label={t("label_pan")} 
              placeholder={t("placeholder_pan")} 
              value={vForm.pan} 
              onChange={handlePanChange} 
              maxLength={10}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck="false"
              autoComplete="off"
              style={{ textTransform: "uppercase" }}
              className="uppercase font-mono"
              error={getFieldError(formErrors, 'pan') || getFieldError(formErrors, 'pan_number')} 
            />
            <div />
          </FormRow>
          <div className="mb-4">
            <FileUpload 
              label={t("label_pan_photo")} 
              accept="image/*,.pdf" 
              multiple
              files={vForm.pan_files} 
              onChange={files => setVForm(f => ({ 
                ...f, 
                pan_files: files, 
                pan_file_ids: (files || []).filter(x => !(x instanceof File) && x.id).map(x => x.id) 
              }))} 
              note={t("note_pan_photo")} 
            />
          </div>
          <FormRow><Field label={t("label_notes")} type="textarea" value={vForm.notes} onChange={sv("notes")} rows={2} /></FormRow>
          <FormRow><Field label={t("label_status")} type="select" value={vForm.status} onChange={sv("status")} options={[{ value: "Active", label: t("opt_active") }, { value: "Inactive", label: t("opt_inactive") }]} /></FormRow>
          <FormActions onSave={saveVendor} onCancel={() => { setModal(null); setEditId(null); setFormErrors({}); }} label={editId ? t("btn_update_vendor") : t("btn_add_vendor")} loading={submitting} />
        </Modal>
      )}
      {modal === "amc" && (
        <Modal title={editId ? t("title_edit_amc") : t("title_new_amc")} onClose={() => { setModal(null); setEditId(null); setFormErrors({}); }}>
          <FormRow cols={2}>
            <Field label={t("label_vendor")} type="select" value={aForm.vendor_id} onChange={sa("vendor_id")} options={[{ value: "", label: t("opt_select_vendor") }, ...vendors.map(v => ({ value: v.id, label: v.name }))]} required error={getFieldError(formErrors, 'vendor_id')} />
            <Field label={t("label_service_type")} value={aForm.service_type} onChange={sa("service_type")} placeholder={t("placeholder_lift")} required error={getFieldError(formErrors, 'service_type')} />
          </FormRow>
          <FormRow cols={2}>
            <Field label={t("label_start_date")} type="date" value={aForm.start_date} onChange={sa("start_date")} />
            <Field label={t("label_end_date")} type="date" value={aForm.end_date} onChange={sa("end_date")} required error={getFieldError(formErrors, 'end_date')} />
          </FormRow>
          <FormRow cols={2}>
            <Field label={t("label_contract_value")} type="number" value={aForm.contract_value} onChange={sa("contract_value")} required error={getFieldError(formErrors, 'contract_value')} />
            <Field label={t("label_billing_cycle")} type="select" value={aForm.billing_cycle} onChange={sa("billing_cycle")} options={[t("opt_monthly"), t("opt_quarterly"), t("opt_yearly")]} />
          </FormRow>
          <FormRow><Field label={t("label_reminder_days")} type="number" value={aForm.reminder_days} onChange={sa("reminder_days")} note={t("note_reminder_days")} /></FormRow>
          <FileUpload 
            label={t("label_upload_docs")} 
            accept="image/*,.pdf" 
            multiple 
            files={aForm.files} 
            onChange={files => setAForm(f => ({ 
              ...f, 
              files, 
              file_ids: (files || []).filter(x => !(x instanceof File) && x.id).map(x => x.id) 
            }))} 
          />
          <FormActions onSave={saveAmc} onCancel={() => { setModal(null); setEditId(null); setFormErrors({}); }} label={editId ? t("btn_update_amc") : t("btn_add_amc")} loading={submitting} />
        </Modal>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label={t("card_total_vendors")} value={stats.total_vendors} icon="users" accent="blue" />
        <MetricCard label={t("card_active_amcs")} value={stats.active_amcs} icon="clipboard" accent="green" />
        <MetricCard label={t("card_expiring")} value={stats.expiring_soon} icon="alert" accent="red" delta={t("desc_within_30")} deltaUp={false} />
        <MetricCard label={t("card_annual_cost")} value={formatCompactRupees(stats.annual_amc_cost)} icon="money" accent="amber" />
      </div>
      <Tabs tabs={[t("tab_vendors"), t("tab_amc")]} active={tab} onChange={setTab} />
      {loading && (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
      {!loading && tab === 0 && (
        <Card>
          <SectionHeader title={t("title_all_vendors")} action={<Btn sm variant="primary" icon="plus" onClick={() => { setEditId(null); setVForm(defV); setModal("vendor"); }}>{t("btn_add_vendor")}</Btn>} />
          <div className="hidden lg:block">
            <DataTable cols={[t("col_vendor"), t("col_category"), t("col_contact"), t("col_phone"), t("col_gst"), t("col_tds"), t("col_pan"), t("col_status"), t("col_actions")]}
              rows={vendors.map(v => [
                <div className="flex items-center gap-2" key={v.id}><div className="w-7 h-7 rounded-lg gradient-primary flex items-center justify-center"><Icon name="vendors" size={12} color="white" /></div><span className="font-semibold text-ink text-sm">{v.name}</span></div>,
                <Badge color="blue">{v.category}</Badge>, v.contact_person || "-", v.phone || "-",
                <span className="text-sm font-mono text-muted">{v.gst_number || "-"}</span>,
                v.tds_applicable === "Yes"
                  ? <span className="text-xs font-semibold text-amber-600">{v.tds_rate ? `${v.tds_rate}%` : "Yes"}</span>
                  : <span className="text-xs text-muted">-</span>,
                <span className="text-sm font-mono text-muted">{maskPan(v.pan_number)}</span>,
                <Badge color={v.status === "Active" ? "green" : "gray"} dot>{v.status}</Badge>,
                <div className="flex gap-1" key={`actions-${v.id}`}>
                  <Btn xs icon="edit" onClick={() => openEditV(v)}>{t("btn_edit")}</Btn>
                  <Btn xs variant="danger" icon="trash" onClick={() => setDelItem({ type: "vendor", id: v.id })}>{t("btn_del")}</Btn>
                </div>
              ])} />
          </div>
          
          {/* Mobile View Vendors */}
          <div className="block lg:hidden space-y-3 mt-4">
            {vendors.length === 0 && (
              <div className="text-center py-10 text-slate-400 text-sm">No vendors found.</div>
            )}
            {vendors.map((v, i) => (
              <IonItemSliding key={v.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                  <div className="flex w-full items-center gap-3 p-3 bg-white dark:bg-slate-900">
                    <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center flex-shrink-0">
                      <Icon name="vendors" size={16} color="white" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{v.name}</div>
                      <div className="text-xs text-muted mt-1">
                        <span className="font-semibold text-slate-500">{v.category}</span><br/>
                        {v.contact_person || v.phone || "No contact info"}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <Badge color={v.status === "Active" ? "green" : "gray"} dot>{v.status}</Badge>
                    </div>
                    
                    <div className="text-slate-300 ml-1">
                      <Icon name="chevron_left" size={14} />
                    </div>
                  </div>
                </IonItem>
                
                <IonItemOptions side="end">
                  <IonItemOption color="primary" onClick={() => openEditV(v)}>
                    <div className="flex flex-col items-center justify-center w-12">
                      <Icon name="edit" size={20} color="white" />
                    </div>
                  </IonItemOption>
                  <IonItemOption color="danger" onClick={() => setDelItem({ type: "vendor", id: v.id })}>
                    <div className="flex flex-col items-center justify-center w-12">
                      <Icon name="trash" size={20} color="white" />
                    </div>
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            ))}
          </div>
        </Card>
      )}
      {!loading && tab === 1 && (
        <Card>
          {amcs.some(a => daysLeft(a.end_date) <= 30 && daysLeft(a.end_date) > 0) && (
            <Alert type="danger" onClose={() => { }}>
              {t("alert_expiring")}
            </Alert>
          )}
          <SectionHeader title={t("title_amc_contracts")} action={<Btn sm variant="primary" icon="plus" onClick={() => { setEditId(null); setAForm(defA); setModal("amc"); }}>{t("btn_new_amc")}</Btn>} />
          <div className="hidden lg:block">
            <DataTable cols={[t("col_contract"), t("col_vendor"), t("col_value"), t("col_cycle"), t("col_expiry"), t("col_days_left"), t("col_status"), t("col_actions")]}
              rows={amcs.map(a => {
                const vn = a.vendor?.name || vendors.find(v => v.id === a.vendor_id)?.name || "-";
                const dl = daysLeft(a.end_date);
                return [
                  <strong className="text-sm" key={a.id}>{a.service_type}</strong>, vn,
                  `₹${Number(a.contract_value).toLocaleString()}`, a.billing_cycle,
                  a.end_date ? formatDateByPreference(a.end_date) : "-",
                  <span className={`font-bold text-sm ${dl < 14 ? "text-red-500" : dl < 30 ? "text-amber-500" : "text-emerald-600"}`}>{dl}d</span>,
                  <Badge color={dl < 0 ? "gray" : dl < 14 ? "red" : dl < 30 ? "amber" : "green"} dot>{dl < 0 ? "Expired" : dl < 14 ? "Critical" : dl < 30 ? "Expiring" : "Active"}</Badge>,
                  <div className="flex gap-1" key={`actions-${a.id}`}>
                    <Btn xs icon="edit" onClick={() => openEditA(a)}>{t("btn_edit")}</Btn>
                    <Btn xs variant="danger" icon="trash" onClick={() => setDelItem({ type: "amc", id: a.id })}>{t("btn_del")}</Btn>
                  </div>
                ];
              })} />
          </div>

          {/* Mobile View AMCs */}
          <div className="block lg:hidden space-y-3 mt-4">
            {amcs.length === 0 && (
              <div className="text-center py-10 text-slate-400 text-sm">No AMC contracts found.</div>
            )}
            {amcs.map((a, i) => {
              const vn = a.vendor?.name || vendors.find(v => v.id === a.vendor_id)?.name || "-";
              const dl = daysLeft(a.end_date);
              
              return (
                <IonItemSliding key={a.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                  <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                    <div className="flex w-full items-center gap-3 p-3 bg-white dark:bg-slate-900">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 text-primary dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Icon name="clipboard" size={16} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-ink truncate">{a.service_type}</div>
                        <div className="text-xs text-muted mt-1 leading-tight">
                          <span className="font-semibold text-slate-500">{vn}</span><br/>
                          {a.end_date ? formatDateByPreference(a.end_date) : "No end date"}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        <div className="text-sm font-bold text-ink">₹{Number(a.contract_value).toLocaleString()}</div>
                        <Badge color={dl < 0 ? "gray" : dl < 14 ? "red" : dl < 30 ? "amber" : "green"} dot>{dl < 0 ? "Expired" : dl < 14 ? "Critical" : dl < 30 ? "Expiring" : "Active"}</Badge>
                      </div>
                      
                      <div className="text-slate-300 ml-1">
                        <Icon name="chevron_left" size={14} />
                      </div>
                    </div>
                  </IonItem>
                  
                  <IonItemOptions side="end">
                    <IonItemOption color="primary" onClick={() => openEditA(a)}>
                      <div className="flex flex-col items-center justify-center w-12">
                        <Icon name="edit" size={20} color="white" />
                      </div>
                    </IonItemOption>
                    <IonItemOption color="danger" onClick={() => setDelItem({ type: "amc", id: a.id })}>
                      <div className="flex flex-col items-center justify-center w-12">
                        <Icon name="trash" size={20} color="white" />
                      </div>
                    </IonItemOption>
                  </IonItemOptions>
                </IonItemSliding>
              )
            })}
          </div>
        </Card>
      )}

      {/* FAB for Mobile */}
      {createPortal(
        <div className="fixed bottom-6 right-6 z-50 lg:hidden pointer-events-auto">
          <button
            onClick={() => {
              if (tab === 0) {
                setEditId(null); setVForm(defV); setModal("vendor");
              } else {
                setEditId(null); setAForm(defA); setModal("amc");
              }
            }}
            className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:scale-105 transition-transform active:scale-95 border-0 cursor-pointer"
          >
            <Icon name="plus" size={24} />
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
